/**
 * DecryptBinaryUseCase
 *
 * Pure-domain wrapper around `textEncryptionService.decryptBinary`. The binary
 * sibling of `DecryptTextUseCase` — it decrypts ArrayBuffer payloads produced
 * by `encryptBinary` (chat image / thumbnail bytes) using the SAME
 * ECDH-derived AES-GCM key the text use cases use. The leading 12-byte IV is
 * split off and used for AES-GCM decryption. Web-first media-crypto design (no
 * Flutter parity — consideration E).
 *
 * Consumers: chat image/thumbnail render (Task 13), Media Details page
 * (Task 14).
 *
 * Pre-condition: the singleton's AES-GCM key has been derived for the intended
 * peer via `InitializeTextEncryptionUseCase.execute`. When the key is null
 * (never-initialized, peer had no `publicJwk`, or derivation failed) the
 * service returns the input ArrayBuffer unchanged (pass-through).
 *
 * Failure contract: a non-conforming / legacy payload (not produced by
 * `encryptBinary` — wrong IV, truncated, or unencrypted Flutter-origin media)
 * fails AES-GCM's auth-tag check and the service rethrows — this use case does
 * not catch. The caller renders the error/placeholder (consideration A/E:
 * legacy media is accepted to fail to decrypt).
 *
 * Service-singleton caveat: `textEncryptionService` has a single derived-key
 * slot shared with the text path. Any caller that interleaves
 * `initialize → decryptBinary` for different peers MUST serialize the pair —
 * see `InitializeTextEncryptionUseCase`'s JSDoc.
 */

import {
  TextEncryptionService,
  textEncryptionService,
} from '@/domain/services/textEncryption/textEncryptionService';

export class DecryptBinaryUseCase {
  private textEncryptionService: TextEncryptionService;

  constructor(service: TextEncryptionService = textEncryptionService) {
    this.textEncryptionService = service;
  }

  /**
   * Decrypt `data` (`[ iv (12 bytes) ‖ ciphertext ]`) using the singleton's
   * currently-derived AES-GCM key. Returns the plaintext ArrayBuffer. When the
   * derived key is null (pass-through mode) returns `data` unchanged. On a
   * SubtleCrypto failure (non-conforming / legacy payload) the service
   * rethrows — this use case does not catch.
   */
  async execute(data: ArrayBuffer): Promise<ArrayBuffer> {
    return this.textEncryptionService.decryptBinary(data);
  }
}

export const decryptBinaryUseCase = new DecryptBinaryUseCase();
